import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './AssignClinicalDaysModal.module.scss';
import Modal from '../../../components/modal/Modal';
import Button from '../../../components/button/Button';
import Dropdown from '../../../components/dropdown/Dropdown';
import CustomDatePicker from '../../../components/customDatePicker/CustomDatePicker';
import Icon from '../../../components/icon/Icon';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

const AssignClinicalDaysModal = ({ isOpen, onClose, studentIds = [], onSuccess }) => {
    const { showToast } = useToasts();

    const [instructorList, setInstructorList] = useState([]);
    const [formData, setFormData] = useState({ instructorId: '', startDate: null, endDate: null });
    const [errors, setErrors] = useState({});
    const [isLoadingInstructors, setIsLoadingInstructors] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setFormData({ instructorId: '', startDate: null, endDate: null });
        setErrors({});

        setIsLoadingInstructors(true);
        axiosClient.get('users/instructors').then((response) => {
            if (response.data?.instructors) {
                setInstructorList(response.data.instructors.map((item) => ({
                    value: item.id,
                    label: `${item.firstName} ${item.lastName}`
                })));
            }
        }).finally(() => setIsLoadingInstructors(false));
    }, [isOpen]);

    const validate = () => {
        const newErrors = {};
        if (!formData.instructorId) newErrors.instructorId = 'Instructor is required';
        if (!formData.startDate) newErrors.startDate = 'Start Date is required';
        if (!formData.endDate) newErrors.endDate = 'End Date is required';
        return newErrors;
    };

    const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${month}-${day}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            await axiosClient.post('allocations/create', {
                studentIds,
                instructorId: formData.instructorId,
                startDate: formatDate(formData.startDate),
                endDate: formatDate(formData.endDate)
            });
            showToast('Clinical days assigned successfully', 'success');
            onSuccess?.();
            onClose();
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal scrollable={false}>
            {(isLoadingInstructors || isSubmitting) && <LoadingOverlay />}
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={styles.header}>
                    <h2 className={styles.title}>
                        Assign Clinical Days — {studentIds.length} Student{studentIds.length > 1 ? 's' : ''} Selected
                    </h2>
                    <button type="button" className={styles.closeBtn} onClick={onClose}>
                        <Icon name="plus" className={styles.crossIcon} />
                    </button>
                </div>

                <div className={styles.section}>
                    <div className={styles.row}>
                        <div className={styles.column100}>
                            <Dropdown
                                label="Instructor *"
                                options={instructorList}
                                value={formData.instructorId}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, instructorId: val }));
                                    if (errors.instructorId) setErrors(prev => ({ ...prev, instructorId: '' }));
                                }}
                                error={errors.instructorId}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.row}>
                        <div className={styles.column50}>
                            <CustomDatePicker
                                label="Start Date *"
                                date={formData.startDate}
                                onChange={(date) => {
                                    setFormData(prev => ({ ...prev, startDate: date }));
                                    if (errors.startDate) setErrors(prev => ({ ...prev, startDate: '' }));
                                }}
                                showIcon={true}
                                minDate={new Date()}
                                maxDate={formData.endDate}
                                error={errors.startDate}
                            />
                        </div>
                        <div className={styles.column50}>
                            <CustomDatePicker
                                label="End Date *"
                                date={formData.endDate}
                                onChange={(date) => {
                                    setFormData(prev => ({ ...prev, endDate: date }));
                                    if (errors.endDate) setErrors(prev => ({ ...prev, endDate: '' }));
                                }}
                                showIcon={true}
                                minDate={formData.startDate || new Date()}
                                error={errors.endDate}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.actions}>
                    <Button label="Cancel" variant="secondary" size="medium" onClick={onClose} />
                    <Button label="Assign" variant="primary" size="medium" type="submit" isLoading={isSubmitting} />
                </div>
            </form>
        </Modal>
    );
};

AssignClinicalDaysModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    studentIds: PropTypes.arrayOf(PropTypes.number),
    onSuccess: PropTypes.func
};

export default AssignClinicalDaysModal;
